/* eslint-disable react/prop-types */
import { useEffect, useState, useMemo } from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectProjectResults, selectSelectedVersion } from '../../project-details/data-access/project.reducer';
import { isWorkbenchNonRerunRun } from '../../../../utils/workbenchRunFilter';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, GraphicComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  deriveExplainabilityFromLegacy,
  EXPLAINABILITY_CONFIG,
  type LegacyExplainabilityEntry
} from '../../Bias/explainabilityEnterprise.utils';
import {
  getPlot1SourceAttributionOption,
  getPlot2ShapTopKOption,
  getPlot4FaithfulnessGaugeOption
} from '../../Bias/explainabilityEnterpriseCharts';

echarts.use([TooltipComponent, GridComponent, LegendComponent, GraphicComponent, BarChart, CanvasRenderer]);

type Entry = { Feature: string; Importance: number; [key: string]: string | number | undefined };

/** Parse "FeatureName (0.42)" or "FeatureName (freq: 0.42)" style string. */
const parseTokenOrFeatureString = (s: string): Entry | null => {
  const trimmed = String(s).trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^(.+?)\s*\(([\d.e+-]+)\)$/);
  if (match) {
    const feature = match[1].trim();
    const importance = parseFloat(match[2]);
    return { Feature: feature, Importance: Number.isFinite(importance) ? importance : 0 };
  }
  return { Feature: trimmed, Importance: 0 };
};

const parseExplainabilityEntries = (input: unknown): Entry[] => {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input
      .map(entry => {
        if (typeof entry === 'string') return parseTokenOrFeatureString(entry);
        if (typeof entry === 'object' && entry !== null) {
          const e = entry as Record<string, unknown>;
          const feature = e.Feature ?? e.feature ?? e.Token ?? e.token ?? e.attribute ?? 'Feature';
          const importance =
            e.Importance ??
            e.importance ??
            e.weight ??
            e.score ??
            e.value ??
            (e as Record<string, unknown>)['SHAP Contribution'] ??
            (e as Record<string, unknown>)['LIME Score'] ??
            (e as Record<string, unknown>)['shap_contribution'] ??
            (e as Record<string, unknown>)['lime_score'] ??
            0;
          const num = Number(importance);
          return { Feature: String(feature), Importance: Number.isFinite(num) ? num : 0, ...(e as object) } as Entry;
        }
        return null;
      })
      .filter(Boolean) as Entry[];
  }
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;
    const nested = obj.top_features ?? obj.features ?? obj.tokens ?? obj.data ?? obj.entries;
    if (Array.isArray(nested)) return parseExplainabilityEntries(nested);
  }
  return [];
};

interface ExplainabilityCardProps {
  customData?: {
    ML_Model_Explainability?: Record<string, any>;
    LLM_Model_Explainability?: Record<string, any>;
  };
}

export const ExplainabilityCard: React.FC<ExplainabilityCardProps> = ({ customData }) => {
  const projectResults = useSelector(selectProjectResults);
  const selectedVersion = useSelector(selectSelectedVersion);

  const [mlEntries, setMlEntries] = useState<Entry[]>([]);
  const [llmEntries, setLlmEntries] = useState<Entry[]>([]);

  const explainabilityRuns = useMemo(() => {
    const runs: Record<string, unknown>[] = [];
    const seen = new Set<string>();

    const push = (candidate: unknown) => {
      if (!candidate || typeof candidate !== 'object') return;
      if (Array.isArray(candidate)) {
        candidate.forEach(item => {
          if (item && typeof item === 'object' && isWorkbenchNonRerunRun(item)) {
            const id = (item as { result_id?: string }).result_id;
            if (id && seen.has(id)) return;
            if (id) seen.add(id);
            runs.push(item as Record<string, unknown>);
          }
        });
      } else if (isWorkbenchNonRerunRun(candidate)) {
        const id = (candidate as { result_id?: string }).result_id;
        if (id && seen.has(id)) return;
        if (id) seen.add(id);
        runs.push(candidate as Record<string, unknown>);
      }
    };

    push(projectResults?.results?.explainability);
    push(projectResults?.feature_results?.explainability);
    push(projectResults?.collection_results?.explainability);
    push(projectResults?.results?.explainability_ml);
    push(projectResults?.results?.explainability_llm);
    return runs;
  }, [projectResults]);

  const explainabilitySources = useMemo(() => {
    const sources: unknown[] = [];

    // If aggregated data from 360-analysis API is provided, add it as a synthetic source
    if (customData) {
      const syntheticSource: Record<string, any> = {};
      if (customData.ML_Model_Explainability) {
        syntheticSource.ML_Model_Explainability = customData.ML_Model_Explainability;
      }
      if (customData.LLM_Model_Explainability) {
        syntheticSource.LLM_Model_Explainability = customData.LLM_Model_Explainability;
      }
      if (Object.keys(syntheticSource).length > 0) {
        sources.push(syntheticSource);
        return sources; // prioritize 360 API data
      }
    }

    if (explainabilityRuns.length > 0) {
      const matched = selectedVersion?.result_id
        ? explainabilityRuns.find(r => r.result_id === selectedVersion.result_id)
        : undefined;
      return [matched ?? explainabilityRuns[0]];
    }

    const push = (candidate: unknown) => {
      if (candidate && typeof candidate === 'object') {
        if (Array.isArray(candidate)) {
          candidate.forEach(item => {
            if (item && typeof item === 'object') sources.push(item);
          });
        } else sources.push(candidate);
      }
    };
    push(projectResults?.results?.explainability);
    push(projectResults?.feature_results?.explainability);
    push(projectResults?.collection_results?.explainability);
    push(projectResults?.results?.explainability_ml);
    push(projectResults?.results?.explainability_llm);
    return sources;
  }, [projectResults, selectedVersion?.result_id, customData, explainabilityRuns]);

  useEffect(() => {
    const ml: Entry[] = [];
    const llm: Entry[] = [];
    const isRecord = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object';

    const extract = (node: unknown) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(extract);
        return;
      }
      if (!isRecord(node)) return;
      const hasData = (o: unknown) =>
        o && typeof o === 'object' && (Array.isArray(o) ? o.length > 0 : Object.keys(o).length > 0);

      const mlCandidates = [
        node.ML_Model_Explainability,
        node.ml_model_explainability,
        node.mlExplainability,
        node.ml_explainability,
        node.ML,
        node.ml
      ].filter(hasData);
      const llmCandidates = [
        node.LLM_Model_Explainability,
        node.llm_model_explainability,
        node.llmExplainability,
        node.llm_explainability,
        node.LLM,
        node.llm
      ].filter(hasData);

      const pushFromItem = (item: Record<string, unknown>, into: Entry[]) => {
        const top = (item.top_features ?? item.top_tokens) as unknown;
        if (top) parseExplainabilityEntries(top).forEach(e => into.push(e));
        const shapContrib = (item.Sensitive_Feature_Contributions ?? item.sensitive_feature_contributions) as unknown;
        if (Array.isArray(shapContrib)) parseExplainabilityEntries(shapContrib).forEach(e => into.push(e));
        const limeInsights = (item.LIME_Feature_Insights ?? item.lime_feature_insights) as unknown;
        if (Array.isArray(limeInsights)) parseExplainabilityEntries(limeInsights).forEach(e => into.push(e));
        if (into.length === 0) parseExplainabilityEntries(item).forEach(e => into.push(e));
      };

      mlCandidates.forEach(c => {
        if (Array.isArray(c)) {
          (c as unknown[]).forEach((item: unknown) => {
            if (isRecord(item)) pushFromItem(item, ml);
          });
        } else parseExplainabilityEntries(c).forEach(e => ml.push(e));
      });
      llmCandidates.forEach(c => {
        if (Array.isArray(c)) {
          (c as unknown[]).forEach((item: unknown) => {
            if (isRecord(item)) pushFromItem(item, llm);
          });
        } else parseExplainabilityEntries(c).forEach(e => llm.push(e));
      });

      if (Array.isArray(node.entries)) node.entries.forEach(extract);
      if (Array.isArray(node.data)) node.data.forEach(extract);
      if (isRecord(node.results) && node.results.explainability) extract(node.results.explainability);
      if (isRecord(node.explainability)) extract(node.explainability);
    };

    explainabilitySources.forEach(extract);
    setMlEntries(ml);
    setLlmEntries(llm);
  }, [explainabilitySources]);

  const derived = useMemo(() => {
    const ml: LegacyExplainabilityEntry[] = mlEntries.map(e => ({ Feature: e.Feature, Importance: e.Importance }));
    const llm: LegacyExplainabilityEntry[] = llmEntries.map(e => ({ Feature: e.Feature, Importance: e.Importance }));
    return deriveExplainabilityFromLegacy(ml, llm);
  }, [mlEntries, llmEntries]);

  const plot1 = useMemo(() => getPlot1SourceAttributionOption(derived.sourceAttribution), [derived.sourceAttribution]);
  const plot2 = useMemo(() => getPlot2ShapTopKOption(derived.shapTopK), [derived.shapTopK]);
  const plot4 = useMemo(() => getPlot4FaithfulnessGaugeOption(derived.meanAopc), [derived.meanAopc]);

  const chartH = 240;

  return (
    <>
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <span className="fw-semibold">XRS: {derived.xrs.toFixed(3)}</span>
            <span className="text-muted">Verdict: {derived.verdict}</span>
            <span className="small text-muted">
              LOW ≤ {EXPLAINABILITY_CONFIG.xrs_low}, MOD ≤ {EXPLAINABILITY_CONFIG.xrs_high}, HIGH &gt;{' '}
              {EXPLAINABILITY_CONFIG.xrs_high}
            </span>
          </div>
        </Card.Body>
      </Card>
      <Row className="g-3 mt-2">
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-2">
              {plot1 ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot1}
                  notMerge
                  lazyUpdate
                  style={{ width: '100%', height: `${chartH}px` }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: chartH }}>
                  <p className="text-muted small mb-0">No source attribution</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-2">
              {plot2 ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot2}
                  notMerge
                  lazyUpdate
                  style={{ width: '100%', height: `${chartH}px` }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: chartH }}>
                  <p className="text-muted small mb-0">No SHAP Top-K</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-2">
              <h6 className="fw-semibold mb-2 small">Plot 3 — LIME Stability</h6>
              <Table striped bordered size="sm" className="mb-0 small">
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Stability</th>
                    <th>Std</th>
                  </tr>
                </thead>
                <tbody>
                  {derived.limeStability.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        No data
                      </td>
                    </tr>
                  ) : (
                    derived.limeStability.map((row, i) => (
                      <tr key={i}>
                        <td>{row.token}</td>
                        <td>{row.status}</td>
                        <td>{row.std.toFixed(3)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body className="p-2">
              {plot4 ? (
                <ReactEChartsCore
                  echarts={echarts}
                  option={plot4}
                  notMerge
                  lazyUpdate
                  style={{ width: '100%', height: `${chartH}px` }}
                  opts={{ renderer: 'canvas' }}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: chartH }}>
                  <p className="text-muted small mb-0">No faithfulness</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ExplainabilityCard;
